<template>
  <q-page padding>
    <q-card class="auto-agendamiento-form" style="max-width: 100vw; height: 90vh; display: flex; flex-direction: column;">
      <!-- Header Fijo -->
      <q-toolbar class="bg-gradient-primary text-white toolbar-fixed">
        <q-icon name="event_available" size="28px" class="q-mr-sm" />
        <q-toolbar-title>
          <div class="text-h6">Auto Agendamiento</div>
          <div class="text-caption text-white-7">
            Pedido {{ agendamiento.C6_NUM }} - {{ agendamiento.A1_NOME }}
          </div>
        </q-toolbar-title>

        <!-- Badge de estado -->
        <q-badge
          :color="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'positive' : 'white'"
          :text-color="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'white' : 'primary'"
          class="q-mr-md"
        >
          <q-icon name="local_shipping" size="16px" class="q-mr-xs" />
          {{ agendamiento.reservado }}/{{ agendamiento.camionescant }}
        </q-badge>
      </q-toolbar>

      <!-- Contenido Scrolleable -->
      <div class="row no-wrap content-scrollable">
        <!-- Columna Principal -->
        <div class="col-12 col-md-8 q-pa-md scroll-area">
          <!-- Loading Skeleton -->
          <div v-if="loading && !agendamiento.C6_NUM" class="q-gutter-md">
            <q-skeleton type="rect" height="200px" class="rounded-borders" />
            <q-skeleton type="rect" height="150px" class="rounded-borders" />
            <q-skeleton type="rect" height="400px" class="rounded-borders" />
          </div>

          <!-- Contenido Real -->
          <template v-else>
            <!-- Sección 1: Datos del Pedido -->
            <q-expansion-item
              default-opened
              icon="description"
              label="Datos del Pedido"
              caption="Información del pedido de venta"
              header-class="bg-grey-2 text-primary text-weight-medium"
              class="q-mb-md rounded-borders overflow-hidden"
            >
              <q-card flat bordered>
                <q-card-section>
                  <div class="row q-col-gutter-md">
                    <div class="col-12 col-sm-6">
                      <GltnInput label="Planta" :model-value="agendamiento.PLANTA" readonly />
                    </div>
                    <div class="col-12 col-sm-6">
                      <GltnInput label="Pedido de venta" :model-value="agendamiento.C6_NUM" readonly />
                    </div>
                    <div class="col-12 col-sm-6">
                      <GltnInput label="Presupuesto" :model-value="agendamiento.C6_USREQVE" readonly />
                    </div>
                    <div class="col-12 col-sm-6">
                      <GltnInput label="Cliente" :model-value="agendamiento.A1_NOME" readonly />
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <!-- Sección 2: Información de Transporte -->
            <q-expansion-item
              default-opened
              icon="local_shipping"
              label="Información de Transporte"
              :caption="`${agendamiento.camionescant} camión(es) - ${agendamiento.reservado} reservado(s)`"
              header-class="bg-grey-2 text-primary text-weight-medium"
              class="q-mb-md rounded-borders overflow-hidden"
            >
              <q-card flat bordered>
                <q-card-section>
                  <div class="row q-col-gutter-md">
                    <div class="col-12 col-sm-6 col-md-4">
                      <GltnInput label="Tipo de camión" :model-value="agendamiento.camionestipo" readonly />
                    </div>
                    <div class="col-12 col-sm-6 col-md-4">
                      <GltnInput label="Carrocería" :model-value="agendamiento.carroceria" readonly />
                    </div>
                    <div class="col-12 col-md-4">
                      <div class="summary-stat">
                        <q-icon :name="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'check_circle' : 'pending'"
                                :color="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'positive' : 'warning'"
                                size="24px" />
                        <div class="q-ml-sm">
                          <div class="text-caption text-grey-7">Estado de Reserva</div>
                          <div class="text-h6 text-weight-bold">
                            {{ agendamiento.reservado }}/{{ agendamiento.camionescant }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <!-- Sección 3: Calendario -->
            <q-expansion-item
              default-opened
              icon="calendar_today"
              label="Seleccione Fecha y Horario"
              caption="Haga clic en un turno disponible (verde) para agendar"
              header-class="bg-grey-2 text-primary text-weight-medium"
              class="rounded-borders overflow-hidden"
            >
              <q-card flat bordered>
                <q-card-section :class="isMobile ? 'q-pa-sm' : 'q-pa-md'">
                  <div v-if="loading" class="calendar-skeleton">
                    <q-skeleton type="rect" height="50px" class="q-mb-md rounded-borders" />
                    <q-skeleton type="rect" height="400px" class="rounded-borders" />
                  </div>
                  <div v-else class="calendar-wrapper">
                    <div v-if="loadingCalendar" class="calendar-loading-overlay">
                      <q-spinner-dots color="primary" size="50px" />
                      <div class="text-caption text-grey-7 q-mt-md">Cargando turnos...</div>
                    </div>
                    <FullCalendar ref="fullCalendar" :options="calendarOptionsComputed" />
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <!-- Resumen para Móvil -->
            <div class="lt-md q-mt-md">
              <q-expansion-item
                icon="summarize"
                label="Resumen del Agendamiento"
                header-class="bg-grey-2 text-primary text-weight-medium"
                class="rounded-borders overflow-hidden mobile-summary"
              >
                <q-card flat bordered>
                  <q-card-section>
                    <div class="summary-row">
                      <span class="text-grey-7">Pedido:</span>
                      <span class="text-weight-medium">{{ agendamiento.C6_NUM }}</span>
                    </div>
                    <div class="summary-row">
                      <span class="text-grey-7">Cliente:</span>
                      <span class="text-caption">{{ agendamiento.A1_NOME }}</span>
                    </div>
                    <q-separator class="q-my-sm" />
                    <div class="summary-row">
                      <span class="text-grey-7">Camiones:</span>
                      <span>{{ agendamiento.camionescant }}</span>
                    </div>
                    <div class="summary-row text-weight-bold">
                      <span>Reservados:</span>
                      <span class="text-primary">{{ agendamiento.reservado }}/{{ agendamiento.camionescant }}</span>
                    </div>
                    <q-linear-progress
                      :value="progressReserva"
                      :color="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'positive' : 'primary'"
                      size="12px"
                      rounded
                      class="q-mt-md"
                    />
                    <div class="text-caption text-center text-grey-7 q-mt-xs">
                      {{ Math.round(progressReserva * 100) }}% completado
                    </div>
                  </q-card-section>
                </q-card>
              </q-expansion-item>
            </div>
          </template>
        </div>

        <!-- Columna Lateral de Resumen (solo desktop) -->
        <div class="col-12 col-md-4 bg-grey-1 q-pa-md gt-sm scroll-area">
          <div class="sticky-summary">
            <!-- Skeleton -->
            <div v-if="loading && !agendamiento.C6_NUM">
              <q-skeleton type="rect" height="300px" class="rounded-borders" />
            </div>

            <template v-else>
              <q-card flat bordered class="summary-card">
                <q-card-section class="bg-primary text-white">
                  <div class="text-h6">
                    <q-icon name="event_available" class="q-mr-sm" />
                    Resumen de Agendamiento
                  </div>
                </q-card-section>

                <q-separator />

                <q-card-section>
                  <div class="text-subtitle2 text-grey-8 q-mb-sm">Pedido</div>
                  <div class="summary-row">
                    <span class="text-grey-7">N° Pedido:</span>
                    <span class="text-weight-medium">{{ agendamiento.C6_NUM }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="text-grey-7">Presupuesto:</span>
                    <span>{{ agendamiento.C6_USREQVE }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="text-grey-7">Cliente:</span>
                    <span class="text-caption">{{ agendamiento.A1_NOME }}</span>
                  </div>

                  <q-separator class="q-my-md" />

                  <div class="text-subtitle2 text-grey-8 q-mb-sm">Transporte</div>
                  <div class="summary-row">
                    <span class="text-grey-7">Tipo:</span>
                    <span>{{ agendamiento.camionestipo }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="text-grey-7">Carrocería:</span>
                    <span>{{ agendamiento.carroceria }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="text-grey-7">Cantidad:</span>
                    <span>{{ agendamiento.camionescant }} camión(es)</span>
                  </div>

                  <q-separator class="q-my-md" />

                  <div class="text-subtitle2 text-grey-8 q-mb-sm">Estado</div>
                  <div class="summary-row text-weight-bold">
                    <span>Reservados:</span>
                    <span class="text-primary">
                      {{ agendamiento.reservado }}/{{ agendamiento.camionescant }}
                    </span>
                  </div>

                  <q-linear-progress
                    :value="progressReserva"
                    :color="agendamiento.reservado >= parseInt(agendamiento.camionescant) ? 'positive' : 'primary'"
                    size="12px"
                    rounded
                    class="q-mt-md"
                  />
                  <div class="text-caption text-center text-grey-7 q-mt-xs">
                    {{ Math.round(progressReserva * 100) }}% completado
                  </div>
                </q-card-section>
              </q-card>

              <!-- Tips -->
              <q-card flat bordered class="q-mt-md">
                <q-card-section class="bg-blue-1">
                  <div class="row items-center">
                    <q-icon name="lightbulb" color="amber" size="24px" class="q-mr-sm" />
                    <span class="text-weight-medium">Instrucciones</span>
                  </div>
                </q-card-section>
                <q-card-section>
                  <ul class="q-pl-md text-caption text-grey-7">
                    <li>Los turnos en <span class="text-positive text-weight-medium">verde</span> están disponibles</li>
                    <li>Los turnos en <span class="text-negative text-weight-medium">rojo</span> están reservados</li>
                    <li>Haga clic en un turno verde para agendar</li>
                    <li>El horario de atención es de 10:00 a 18:00 hs</li>
                  </ul>
                </q-card-section>
              </q-card>
            </template>
          </div>
        </div>
      </div>

      <!-- Footer Fijo -->
      <q-separator />
      <q-card-actions class="q-pa-md actions-fixed bg-white">
        <div class="row full-width q-gutter-sm">
          <div class="col">
            <q-btn
              outline
              label="Volver"
              color="grey-7"
              icon="arrow_back"
              @click="$inertia.visit(route('autoagendamiento.index'))"
              class="full-width"
            />
          </div>
          <div class="col">
            <q-btn
              unelevated
              label="Ver Turnos Reservados"
              color="primary"
              icon="event_note"
              :disable="agendamiento.reservado === 0"
              @click="verTurnosReservados"
              class="full-width"
            />
          </div>
        </div>
      </q-card-actions>
    </q-card>
  </q-page>
</template>

<script setup>
import ExternalLayout from '@/layouts/ExternalLayout.vue';
import AutoAgendamientoService from '@/services/AutoAgendamientoService.js';
import FullCalendar from '@fullcalendar/vue3';
import GltnInput from '@/components/form/GltnInput.vue';
import TotvsLogisticaService from '@/services/TotvsLogisticaService.js';
import esLocale from '@fullcalendar/core/locales/es';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import { onMounted, ref, computed } from 'vue';
import { format, formatISO, parse } from 'date-fns';
import { useQuasar } from 'quasar';
import { route } from 'ziggy-js';

defineOptions({ layout: ExternalLayout })

const $q = useQuasar();
const fullCalendar = ref(null);
const loading = ref(false);
const loadingCalendar = ref(false);
const loadingStatus = ref('Cargando...');
const props = defineProps({
  id: String,
});
const agendamiento = ref({});
const agendamientos = ref([]);
const camiones = ref(0);
let calendarApi = null;

// Responsive detection
const isMobile = computed(() => $q.screen.lt.md);

// Progress de reserva
const progressReserva = computed(() => {
  if (!agendamiento.value.camionescant) return 0;
  return parseInt(agendamiento.value.reservado) / parseInt(agendamiento.value.camionescant);
});

const fields = {
  PLANTA: 'Planta',
  C6_NUM: 'Pedido de venta',
  C6_USREQVE: 'Presupuesto',
  A1_NOME: 'Cliente',
  camionestipo: 'Tipo de camion',
  carroceria: 'Carrocería',
  camionescant: 'Cantidad de camiones',
  reservado: 'Reservado',
};

// Funciones para obtener eventos
const getHorariosDisponiblesEvent = async (info) => {
  try {
    const fecha = format(info.start, 'yyyy-MM-dd');
    const res = await TotvsLogisticaService.getHorariosDisponibles({
      fecha,
      listaProcessosRodoviarioId: agendamiento.value.IDJAN,
      localId: agendamiento.value.IDLOC,
      unidadeId: agendamiento.value.IDLOG,
    });
    const events = res.map(({ id, start, end, agendaId }) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const startTime = format(startDate, 'HH:mm');
      const endTime = format(endDate, 'HH:mm');
      return {
        title: `Disponible`,
        id,
        start,
        end,
        extendedProps: {
          esAgenda: true,
          agendaId,
        },
        backgroundColor: '#66bb6a',
        borderColor: '#2e7d32',
      };
    });
    return events;
  } catch (error) {
    console.error('Error cargando horarios disponibles:', error);
    return [];
  }
};

const getAgendaByPedidoEvent = async (info) => {
  try {
    const items = await TotvsLogisticaService.getAgendaByPedido({
      pedido: agendamiento.value.C6_NUM,
      transportadoraId: agendamiento.value.IDTRANSP,
      unidadeId: agendamiento.value.IDLOG,
    });
    const events = items.map(({ id, start, end }) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const startTime = format(startDate, 'HH:mm');
      const endTime = format(endDate, 'HH:mm');
      return {
        title: `Reservado`,
        id,
        start,
        end,
        extendedProps: {
          esAgenda: false,
        },
        backgroundColor: '#ef5350',
        borderColor: '#c62828',
      };
    });
    agendamientos.value = items;
    return events;
  } catch (error) {
    console.error('Error cargando agenda por pedido:', error);
    return [];
  }
};

// Opciones base del calendario
const calendarOptionsBase = ref({
  plugins: [timeGridPlugin, interactionPlugin, dayGridPlugin, listPlugin],
  allDaySlot: false,
  locale: esLocale,
  nowIndicator: true,
  lazyFetching: false,
  selectConstraint: 'businessHours',
  slotDuration: '00:30',
  validRange: {
    start: new Date(),
  },
  businessHours: {
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '10:00',
    endTime: '18:00',
  },
  loading: (isLoading) => {
    loadingCalendar.value = isLoading;
  },
  eventSources: [
    {
      id: 'ocupado',
      events: (info) => getAgendaByPedidoEvent(info),
    },
    {
      id: 'libre',
      events: (info) => getHorariosDisponiblesEvent(info),
    },
  ],
});

// Opciones del calendario responsive
const calendarOptionsComputed = computed(() => ({
  ...calendarOptionsBase.value,
  initialView: isMobile.value ? 'listMonth' : 'timeGridDay',
  headerToolbar: isMobile.value
    ? {
        left: 'prev,next',
        center: 'title',
        right: 'listMonth,timeGridDay',
      }
    : {
        left: 'prev,next today',
        center: 'title',
        right: 'listMonth,timeGridDay,dayGridMonth',
      },
  height: isMobile.value ? 'auto' : '600px',
  contentHeight: isMobile.value ? 'auto' : undefined,
  eventClick: (info) => {
    if (!info.event.extendedProps.esAgenda) return;
    confirmAgendamiento(info);
  },
  dateClick: async (info) => {
    if (!isMobile.value) {
      calendarApi.changeView('timeGridDay', info.dateStr);
    }
  },
}));

onMounted(async () => {
  loading.value = true;
  calendarApi = fullCalendar.value.getApi();
  loadingStatus.value = 'Cargando agendamiento...';
  agendamiento.value = await AutoAgendamientoService.show(props.id);
  camiones.value = parseInt(agendamiento.value.C5_CAMCANT);
  const fechaDisponibilidad = agendamiento.value.fecha_disponibilidad ? parse(
    agendamiento.value.fecha_disponibilidad,
    'yyyyMMdd',
    new Date()
  ) : new Date();
  calendarOptionsBase.value.validRange.start = fechaDisponibilidad;
  calendarApi.gotoDate(fechaDisponibilidad);
  await getProcessoRodoviario();
  await getTotalAgendaByPedido(agendamiento.value.C6_NUM);
  calendarApi.refetchEvents();

  loading.value = false;
});

const getProcessoRodoviario = async () => {
  const res = await TotvsLogisticaService.getProcessoRodoviario({
    id: agendamiento.value.IDJAN,
    unidadeId: agendamiento.value.IDLOG
  });
  calendarOptionsBase.value.slotDuration = `00:${res.duracao}`;
};

const getHorariosDisponibles = async (
  info,
  successCallback,
  failureCallback
) => {
  try {
    const fecha = format(info.start, 'yyyy-MM-dd');
    const res = await TotvsLogisticaService.getHorariosDisponibles({
      fecha,
      listaProcessosRodoviarioId: agendamiento.value.IDJAN,
      localId: agendamiento.value.IDLOC,
      unidadeId: agendamiento.value.IDLOG,
    });
    const events = res.map(({ id, start, end, agendaId }) => {
      return {
        title: 'Turno Disponible',
        id,
        start,
        end,
        extendedProps: {
          esAgenda: true,
          agendaId,
        },
        backgroundColor: '#66bb6a',
        borderColor: '#2e7d32',
      };
    });
    successCallback(events);
  } catch (error) {
    failureCallback(error);
  }
};

const getAgendaByPedido = async (info, successCallback, failureCallback) => {
  try {
    const items = await TotvsLogisticaService.getAgendaByPedido({
      pedido: agendamiento.value.C6_NUM,
      transportadoraId: agendamiento.value.IDTRANSP,
      unidadeId: agendamiento.value.IDLOG,
    });
    const events = items.map(({ id, start, end }) => {
      return {
        title: 'Turno Reservado',
        id,
        start,
        end,
        extendedProps: {
          esAgenda: false,
        },
        backgroundColor: '#ef5350',
        borderColor: '#c62828',
      };
    });
    agendamientos.value = items;
    successCallback(events);
  } catch (error) {
    failureCallback(error);
  }
};

const getTotalAgendaByPedido = async (pedido) => {
  const total = await TotvsLogisticaService.getTotalAgendaByPedido({
    pedido,
    transportadoraId: agendamiento.value.IDTRANSP,
    unidadeId: agendamiento.value.IDLOG,
  });
  agendamiento.value.reservado = total;
};
const confirmAgendamiento = async (info) => {
  const { start } = info.event;
  const fecha = format(start, 'dd-MM-yyyy HH:mm');
  const camCant = parseInt(agendamiento.value.camiones_cant);
  const reservado = parseInt(agendamiento.value.reservado);
  if (reservado === camCant) {
    $q.notify({
      type: 'negative',
      message:
        'No hay cupo disponible. La cantidad de camiones ya fue reservada.',
    });
    return;
  }
  $q.dialog({
    title: 'Confirmar',
    message: `Usted está por registrar un turno para un camión para la fecha ${fecha}. Considere que el mismo no podrá ser modificado. Desea continuar?`,
    cancel: true,
    persistent: true,
  }).onOk(() => {
    createAgendamiento(info);
  });
};
const createAgendamiento = async (info) => {
  const {
    start,
    end,
    extendedProps: { agendaId },
  } = info.event;
  const horarioInicio = formatISO(start);
  const horarioFim = formatISO(end);
  loading.value = true;
  loadingStatus.value = 'Registrando agendamiento...';
  const data = {
    clienteId: agendamiento.value.C5_CLIENTE,
    unidadeId: agendamiento.value.IDLOG,
    transportadoraId: '',
    transportadora: null,
    processoRodoviarioId: agendamiento.value.IDJAN,
    ajudanteId: null,
    motoristaId: null,
    motorista: null,
    veiculoId: null,
    veiculo: { id: null, veiculoId: null, tipoVeiculo: 'TRUCK' },
    fornecedorId: null,
    fornecedor: null,
    produtoId: null,
    produto: null,
    cubagem: null,
    peso: 1,
    localId: agendamiento.value.IDLOC,
    quantidadeVolumes: 1,
    caracteristicaPeso: 'BRUTO',
    documentos: [
      {
        pais: 'AR',
        tipo: 'PEDIDO',
        numero: agendamiento.value.C6_NUM,
        destinatario: null,
        data: '',
        serie: null,
        emissor: null,
        isProcessing: {
          closed: false,
          currentObservers: null,
          observers: [],
          isStopped: false,
          hasError: false,
          thrownError: null,
          _value: false,
        },
      },
    ],
    horarioInicio,
    horarioFim,
    agendaId,
    listaItensObrigatorios: [
      '1865b087-d70e-4d8b-87a1-15a94b903c5b',
      'a0725cb8-02e0-45eb-b9c0-6bced4135d4c',
      '9b0d418a-2961-4a41-9a8a-907c408f9ebe',
    ],
    observacao: 'observaciones',
    listaVeiculos: [{ id: null, veiculoId: null, tipoVeiculo: 'TRUCK' }],
    listaAjudantes: null,
    semirreboquesId: [],
  };
  try {
    await TotvsLogisticaService.storeAgenda(data);
    await getTotalAgendaByPedido(agendamiento.value.C6_NUM);
    calendarApi.refetchEvents();
    $q.notify({
      type: 'positive',
      message: 'Turno agendado correctamente',
    });
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: 'Error al registrar el agendamiento',
    });
  } finally {
    loading.value = false;
  }
};

const verTurnosReservados = () => {
  if (!calendarApi) return;
  calendarApi.changeView('listMonth');
  $q.notify({
    type: 'info',
    message: `Mostrando ${agendamiento.value.reservado} turno(s) reservado(s)`,
    icon: 'event_note',
  });
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.auto-agendamiento-form {
  border-radius: 16px;
  overflow: hidden;

  .bg-gradient-primary {
    background: linear-gradient(
      135deg,
      var(--q-primary) 0%,
      color.adjust(#1976d2, $lightness: -10%) 100%
    );
  }

  .toolbar-fixed {
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .content-scrollable {
    flex: 1;
    overflow: hidden;
  }

  .scroll-area {
    overflow-y: auto;
    max-height: 100%;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }
  }

  .actions-fixed {
    flex-shrink: 0;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  }
}

.sticky-summary {
  position: sticky;
  top: 20px;
}

.summary-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  span:first-child {
    flex: 1;
  }

  span:last-child {
    text-align: right;
    word-break: break-word;
  }
}

.summary-stat {
  display: flex;
  align-items: center;
  padding: 12px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8eaf6 100%);
  border-radius: 8px;
  border-left: 4px solid var(--q-primary);
}

.calendar-wrapper {
  min-height: 600px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  position: relative;

  .calendar-loading-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  :deep(.fc) {
    font-size: 0.9em;

    .fc-toolbar {
      flex-wrap: wrap;
      gap: 8px;
      padding: 8px;
      background: linear-gradient(135deg, #f5f5f5 0%, #e8eaf6 100%);
      border-radius: 8px;
      margin-bottom: 16px;

      .fc-toolbar-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1976d2;
      }

      .fc-button {
        border-radius: 6px;
        text-transform: none;
        font-weight: 500;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }
    }

    .fc-event {
      border-radius: 4px;
      padding: 2px 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        filter: brightness(1.1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transform: translateY(-1px);
      }

      &[style*="green"] {
        background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%) !important;
        border-color: #2e7d32 !important;
      }

      &[style*="red"] {
        background: linear-gradient(135deg, #ef5350 0%, #e53935 100%) !important;
        border-color: #c62828 !important;
      }
    }

    .fc-non-business {
      background: rgba(0, 0, 0, 0.03);
    }

    .fc-timegrid-now-indicator-line {
      border-color: #f44336;
      border-width: 2px;
    }
  }
}

.calendar-skeleton {
  padding: 16px;
}

:deep(.q-expansion-item) {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &.q-expansion-item--expanded {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .q-expansion-item__container .q-item {
    min-height: 56px;
  }
}

.text-white-7 {
  color: rgba(255, 255, 255, 0.7);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.summary-card {
  animation: fadeIn 0.4s ease-out;
}

@media (max-width: 1023px) {
  .auto-agendamiento-form {
    border-radius: 0;
    height: 100vh;
  }

  .sticky-summary {
    position: relative;
    top: 0;
  }

  .calendar-wrapper {
    min-height: 400px;
    padding: 8px;

    :deep(.fc) {
      font-size: 0.85em;

      .fc-toolbar {
        flex-direction: column;

        .fc-toolbar-chunk {
          width: 100%;
          justify-content: center;
          margin-bottom: 8px;
        }

        .fc-toolbar-title {
          font-size: 1rem;
        }
      }

      .fc-button {
        padding: 4px 8px;
        font-size: 0.85rem;
      }
    }
  }
}

@media (max-width: 599px) {
  .calendar-wrapper {
    min-height: 350px;

    :deep(.fc) {
      .fc-toolbar-title {
        font-size: 0.9rem;
      }

      .fc-view-harness {
        min-height: 300px;
      }
    }
  }
}
</style>
